import { api } from './api.ts';
import type { EnvPayload } from '../../shared/api.ts';

/**
 * The staging warning, as a promise a call site can await.
 *
 * Staging is a byte-identical copy of production, so a tester has no way of
 * knowing which one they are on, and every build they start spends real AI
 * credits on a deck nobody will ever open. `StagingGuardHost` (mounted once in
 * App.tsx) renders the dialog; everything else in the client talks to this
 * module and never to the host.
 *
 * WHY NOT INTERCEPT IN `api.ts`. That would be one place instead of eight, and
 * it does not work: the two paths that matter most — a headless build (multipart
 * `fetch`) and a chat turn (an SSE `fetch`) — do not go through the shared api
 * client at all. A guard that covered everything except builds and chat would be
 * worse than none, because it would look complete.
 *
 * The server refuses these routes independently
 * (`routes/helpers.ts` → `refuseUnacknowledgedStagingSpend`), so a stale tab or
 * a direct call gets a sentence rather than a silent spend. This half is about
 * asking the person; that half is about not trusting that we did.
 */

let envPromise: Promise<EnvPayload> | null = null;

/** The deploy's own answer, fetched once. FAILS OPEN: a studio that cannot
 *  reach `/api/env` behaves as production, because blocking real work on a
 *  broken health endpoint is a worse failure than missing a warning on a copy
 *  nobody is using. */
export function stagingEnv(): Promise<EnvPayload> {
  if (!envPromise) {
    envPromise = api
      .get<EnvPayload>('/api/env')
      .catch(() => ({ staging: false, liveUrl: 'https://studio.rkhdigital.com' }) satisfies EnvPayload);
  }
  return envPromise;
}

/** What the dialog is currently asking about, for the host to render. */
export type StagingPrompt = { what: string; liveUrl: string };

type Waiting = { prompt: StagingPrompt; settle: (go: boolean) => void };

const queue: Waiting[] = [];
let current: Waiting | null = null;
const listeners = new Set<(p: StagingPrompt | null) => void>();

function announce() {
  for (const fn of listeners) fn(current?.prompt ?? null);
}

function advance() {
  current = queue.shift() ?? null;
  announce();
}

/** The host subscribes; returns an unsubscribe. */
export function onStagingPrompt(fn: (p: StagingPrompt | null) => void): () => void {
  listeners.add(fn);
  fn(current?.prompt ?? null);
  return () => {
    listeners.delete(fn);
  };
}

/** Answer the open prompt. Only `StagingGuardHost` calls these. */
export function resolveStagingPrompt(go: boolean): void {
  const settling = current;
  current = null;
  settling?.settle(go);
  advance();
}

/**
 * Ask before spending. Resolves `true` to go ahead, `false` to abandon.
 *
 * `what` is a GERUND phrase — "building this deck", "importing this case
 * study" — because it is dropped into the middle of the dialog's own sentence:
 * "…and building this deck costs exactly what it would on the live site." It
 * used to go on the button instead, which put a caller's copy inside a
 * fixed-width footer and burst the dialog.
 *
 * Off staging this resolves `true` without rendering anything, so a call site
 * pays one already-settled await and nothing else. It asks EVERY time, on
 * purpose (Alex, 2026-08-20): remembering the answer for the session would make
 * the dialog a formality by the third click, and it is the repetition that
 * carries the message.
 */
export async function confirmStagingSpend(what: string): Promise<boolean> {
  const env = await stagingEnv();
  if (!env.staging) return true;
  return new Promise<boolean>((settle) => {
    const waiting: Waiting = { prompt: { what, liveUrl: env.liveUrl }, settle };
    if (current) queue.push(waiting);
    else {
      current = waiting;
      announce();
    }
  });
}

/**
 * The header that tells the server a person answered. Spread into the request
 * the call site was about to make:
 *
 *   headers: { ...(await stagingAckHeaders()), 'Content-Type': 'application/json' }
 *
 * Empty off staging, so it adds nothing to a production request. Not a security
 * control: it stops an accidental build, a stale tab and a forgotten script,
 * which is the whole brief.
 */
export async function stagingAckHeaders(): Promise<Record<string, string>> {
  const env = await stagingEnv();
  return env.staging ? { 'x-studio-staging-ack': '1' } : {};
}
